import React, { useState } from 'react';
import Modal, { ModalDirectionOptions } from '../../components/Modal';
import {
  calcInfusionSpeed,
  getDripRate,
  formatNumber,
  getTimeUnitString,
  calcDripDosageByMl
} from '../../Resus/medicationUtils';
import { useResusContext } from '../../Resus/ResusContext';
import { Drip, AdministrationDetails } from '../types';
import { useTranslation } from 'react-i18next';

interface DripInfusionSpeedConfirmationDialogProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  drip: Drip;
  onConfirm: (administrationDetails?: AdministrationDetails) => void;
}

const DripInfusionSpeedConfirmationDialog: React.FC<DripInfusionSpeedConfirmationDialogProps> = ({
  isOpen,
  setIsOpen,
  drip,
  onConfirm,
}) => {
  const { weight } = useResusContext();
  const { t } = useTranslation(['resus']);
  const [dosagePrimaryPerTimeValue, setDosagePrimaryPerTimeValue] = useState<string>(String(getDripRate(drip)));
  const [mlPerHourValue, setMlPerHourValue] = useState<string>(String(calcInfusionSpeed(drip, weight)))

  const formatInputValue = (value: string): string => {
    if (value === '') return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return numValue === 0 ? '0' : formatNumber(numValue);
  };

  const handlePrimaryValueChange = (value: string) => {
    const newDrip = drip.dose_per_kg_per_min ? { ...drip, dose_per_kg_per_min: Number(value) } :
      { ...drip, dose_per_kg_per_hour: Number(value) };

    setDosagePrimaryPerTimeValue(value);
    setMlPerHourValue(calcInfusionSpeed(newDrip, weight));
  };

  const handleMlValueChange = (value: string) => {
    setDosagePrimaryPerTimeValue(calcDripDosageByMl(Number(value), drip, weight || NaN));
    setMlPerHourValue(value);
  };

  const createAdministartionDetails = (): AdministrationDetails => {
    const dripLog = formatDripLog();
    return {
      medicalSectionLog: dripLog,
      resusLog: `${drip.name}: ${dripLog}`
    }
  };

  const formatDripLog = (): string => {
    const timeUnit = drip.dose_per_kg_per_min ? 'min' : 'hour';
    return `${formatInputValue(dosagePrimaryPerTimeValue)} ${drip.dose_unit}/kg/${timeUnit}, ${formatInputValue(mlPerHourValue)} ml/hr`;
  };

  const handleConfirm = () => {
    onConfirm(createAdministartionDetails());
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t('medicationDialogs.dripInfusionSpeed.title')}
      direction={ModalDirectionOptions.RTL}
      primaryButton={{
        text: t('medicationDialogs.dripInfusionSpeed.confirm'),
        onClick: handleConfirm,
      }}
      secondaryButton={{
        text: t('medicationDialogs.dripInfusionSpeed.cancel'),
        onClick: () => setIsOpen(false),
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <div style={{ marginBottom: '15px' }}>
          <div style={{ marginBottom: '8px' }}>
            {t('medicationDialogs.dripInfusionSpeed.changeDefaultDose')}
          </div>
          <div style={{ fontSize: '0.9em' }}>
            {t('medicationDialogs.dripInfusionSpeed.unitsUpdateNote')}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ width: '120px', alignSelf: 'center' }}>
            {t('medicationDialogs.dripInfusionSpeed.dosePerKg', {
              unit: drip.dose_unit,
              timeUnit: getTimeUnitString(drip)
            })}
          </div>
          <input
            type="number"
            value={dosagePrimaryPerTimeValue}
            onChange={(e) => handlePrimaryValueChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            step="0.1"
            style={{
              width: '120px',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '120px', alignSelf: 'center' }}>
            {t('medicationDialogs.dripInfusionSpeed.ratePerHour')}
          </div>
          <input
            type="number"
            value={mlPerHourValue}
            onChange={(e) => handleMlValueChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            step="0.5"
            style={{
              width: '120px',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DripInfusionSpeedConfirmationDialog;